// ═══════════════════════════════════════════════════════════════════════
// ONE crash-event extraction for every crash surface — pure, tested.
//
// Two events can announce the crash: the SYNTHETIC `race_crashed` (built
// from the Go `crash:occurred` broadcast at the wall-clock crash instant,
// payload `{ crashMultiplier }`) and the engine's `race_ended` row
// (payload `{ crash_multiplier }`, trailing by up to a couple of seconds).
// raceEvents are sorted ascending by timestamp, so a naive first-match
// `.find()` returns the race_crashed event — and if its payload is absent
// (older client, stripped normalization, malformed broadcast) the settled
// M in race_ended stayed invisible forever: the hero stamped its 1.00x
// floor and later snapped to the real crash point (the stamp flash).
//
// This helper scans ALL crash-announcing events and returns the first
// USABLE multiplier (finite, ≥ 1 — the same validity rule as
// crashHeroClamp.resolveHeroCeiling / useVisualCrash.resolveCrashHold),
// alongside whether a crash is known at all.
// ═══════════════════════════════════════════════════════════════════════

type CrashEventLike = Readonly<{
  eventType: string
  payload?: unknown
}>

export type CrashEventInfo = Readonly<{
  /** A race_crashed / race_ended event exists — the round is over. */
  crashKnown: boolean
  /** The first usable crash multiplier across those events, if any. */
  crashedMultiplier: number | undefined
}>

const isCrashEventType = (eventType: string): boolean =>
  eventType === 'race_crashed' || eventType === 'race_ended'

const usableMultiplier = (payload: unknown): number | undefined => {
  if (typeof payload !== 'object' || payload === null) return undefined
  const p = payload as { crashMultiplier?: unknown; crash_multiplier?: unknown }
  const raw = p.crashMultiplier ?? p.crash_multiplier
  return typeof raw === 'number' && Number.isFinite(raw) && raw >= 1 ? raw : undefined
}

export function resolveCrashEventInfo(raceEvents: readonly CrashEventLike[]): CrashEventInfo {
  let crashKnown = false
  for (const event of raceEvents) {
    if (!isCrashEventType(event.eventType)) continue
    crashKnown = true
    const m = usableMultiplier(event.payload)
    if (m !== undefined) return { crashKnown: true, crashedMultiplier: m }
  }
  return { crashKnown, crashedMultiplier: undefined }
}
